/**
 * PERSONAL — vägen in i firman för alla utom chefen själv.
 *
 * UTAN DEN HÄR SKÄRMEN BESTÅR FIRMAN AV EN PERSON. CREATE_COMPANY speglar
 * skaparen som manager och ägare, och sedan finns ingen väg vidare: de fem
 * gubbarna kommer aldrig in, ingen bokförare och ingen inhyrd kan läggas upp.
 * Kontraktet har rutterna — ADD_MEMBER, SET_MEMBER_ROLE och REMOVE_MEMBER — och
 * de var byggda i workern innan den här filen fanns.
 *
 * ATT TA BORT ÄR LIKA VIKTIGT SOM ATT LÄGGA TILL, OCH DET KOSTAR PENGAR.
 * Beslut 18: faktureringen räknar UPPLAGDA personer med en betald roll vid
 * periodens stängning, inte personer som rapporterat. Den som slutade i mars
 * kostar därför 119 kr i april också, ända tills någon tar bort honom här.
 * Priset står på varje rad av just det skälet.
 *
 * ÄGAREN GÅR INTE ATT RÖRA (beslut 14). Han kan varken degraderas eller tas
 * bort medan han äger firman — servern vägrar med OWNER_MUST_BE_MANAGER och
 * triggrarna i schema/d1.sql står kvar bakom den. Skärmen ERBJUDER därför
 * ingenting på hans rad; ett gränssnitt som frågar och sedan får nej är sämre än
 * ett som aldrig frågar. Vägen är TRANSFER_OWNERSHIP, som inte är byggd — se
 * LUCKOR i överlämningen.
 *
 * INBJUDAN SKICKAR INGET MEJL. worker/src/modules/company/members.ts skapar
 * kontoraden och medlemskapet och ingenting mer. Personen måste själv öppna
 * appen och skriva sin mejladress för att få sin inloggningslänk. Det står
 * utskrivet efter VARJE lyckad inbjudan — utan den meningen lägger chefen upp
 * fem man och undrar sedan varför ingen av dem loggar in.
 *
 * BOKFÖRAREN SER LISTAN, INTE KNAPPARNA. Han står i FULL_VIEW_ROLES och når
 * skärmen, men de tre rutterna är MANAGER_ONLY och han förekommer inte i EN
 * ENDA rad i SYNC_RULES (beslut 07). Samma gräns som i attesten och i NyOrder.
 *
 * OP_ID MYNTAS EN GÅNG OCH ÄNDRAS ALDRIG (beslut 09). När en knapp trycks fryses
 * en PLAN — op_id OCH hela kroppen — och fälten låses. Ett omtag skickar SAMMA
 * op_id med SAMMA kropp och spelas upp ur account_op_log i stället för att bjuda
 * in personen två gånger; en ÄNDRAD kropp med samma op_id ger DUPLICATE_OP.
 * Planen släpps därför i exakt två lägen: när servern SVARAT och avvisat, och
 * när användaren själv väljer att ändra uppgifterna. Efter ett NÄTFEL ligger den
 * kvar, för då vet vi inte om anropet gick fram.
 */

import { useState } from 'react';
import { PRICE_PER_USER_ORE } from '@delat/types';
import type {
  AccountId,
  AddMemberRequest,
  MemberRow,
  OpId,
  Role,
  SetMemberRoleRequest,
} from '@delat/types';
import {
  Falt,
  Felruta,
  Knapp,
  Laddar,
  Personrad,
  Tomtext,
  Valfalt,
  Yta,
  initialer,
} from '../ui';
import { atgardFor } from '../api';
import type { Klartidfel } from '../api';
import { visaOren } from '../app/datum';
import { useInloggad } from '../app/session';
import {
  ROLLVALEN,
  arEpost,
  arOrgnummer,
  bjudIn,
  bytRoll,
  faltFor,
  kostarPengar,
  nyttOpId,
  prisbesked,
  rensaEpost,
  rensaOrgnummer,
  rollrubrik,
  taBort,
  tillOrgnummer,
  usePersonallager,
  visaOrgnummer,
} from './personal-data';

/** Den frusna inbjudan. Ett omtag måste skicka BYTE för BYTE samma kropp. */
interface Inbjudningsplan {
  readonly op_id: OpId;
  readonly begaran: AddMemberRequest;
}

interface Rollplan {
  readonly kontoId: AccountId;
  readonly op_id: OpId;
  readonly begaran: SetMemberRoleRequest;
}

interface Bortplan {
  readonly kontoId: AccountId;
  readonly op_id: OpId;
}

/** Det som ska sägas efter en lyckad inbjudan, och som INTE får försvinna. */
interface Upplagd {
  readonly namn: string;
  readonly epost: string;
}

const LAST_TEXT = 'Prenumerationen är pausad. Inget nytt kan sparas.';

const radtext = (m: MemberRow): string => {
  const delar: string[] = [rollrubrik(m.role)];
  if (m.is_owner) delar.push('äger firman');
  if (m.guest_org_number !== null) delar.push(visaOrgnummer(m.guest_org_number));
  delar.push(m.email);
  return delar.join(' · ');
};

export const Personal = () => {
  const { firmaId, arChef, arLast } = useInloggad();
  const [omgang, sattOmgang] = useState(0);
  const lage = usePersonallager(firmaId, omgang);

  /* Inbjudan */
  const [epost, sattEpost] = useState('');
  const [namn, sattNamn] = useState('');
  const [roll, sattRoll] = useState<Role>('worker');
  const [orgnr, sattOrgnr] = useState('');
  const [inbjudan, sattInbjudan] = useState<Inbjudningsplan | null>(null);
  const [upplagd, sattUpplagd] = useState<Upplagd | null>(null);

  /* Den rad som är öppen för ändring */
  const [vald, sattVald] = useState<AccountId | null>(null);
  const [valdRoll, sattValdRoll] = useState<Role>('worker');
  const [valdOrgnr, sattValdOrgnr] = useState('');
  const [rollplan, sattRollplan] = useState<Rollplan | null>(null);
  const [bortplan, sattBortplan] = useState<Bortplan | null>(null);
  const [bekraftaBort, sattBekraftaBort] = useState(false);

  const [sparar, sattSparar] = useState(false);
  const [fel, sattFel] = useState<Klartidfel | null>(null);
  const [faltfel, sattFaltfel] = useState<Readonly<Record<string, string>>>({});

  const rensaBesked = (): void => {
    sattFel(null);
    sattFaltfel({});
  };

  /**
   * Var felet ska synas, och om planen ska släppas. Texten är ALLTID
   * kontraktets egen — api/fel.ts hämtar den ur shared/errors.ts.
   */
  const visaFel = (nytt: Klartidfel, slappPlanen: () => void): void => {
    const brist = faltFor(nytt);
    if (brist !== null) {
      sattFaltfel({ [brist.falt]: brist.text });
      slappPlanen();
      return;
    }
    sattFel(nytt);
    // Servern har SVARAT och sagt nej -> ingenting blev till, och fälten får
    // öppnas igen. Vid ett nätfel (retry) eller ett låsläge (hold) vet vi inte
    // att anropet föll ofarligt: då ligger planen kvar och bär samma op_id.
    if (nytt.slag === 'api' && atgardFor(nytt) === 'give_up') slappPlanen();
  };

  const laddaOm = (): void => sattOmgang((n) => n + 1);

  /* == Inbjudan ========================================================== */

  const byggInbjudan = (): Inbjudningsplan | null => {
    const brister: Record<string, string> = {};
    const adress = rensaEpost(epost);
    const rensatNamn = namn.trim();
    const siffror = rensaOrgnummer(orgnr);

    if (adress === '') brister['email'] = 'Skriv personens mejladress.';
    else if (!arEpost(adress)) brister['email'] = 'Det där ser inte ut som en mejladress.';
    if (rensatNamn === '') brister['name'] = 'Skriv personens namn.';
    if (roll === 'guest' && !arOrgnummer(siffror)) {
      brister['guest_org_number'] =
        siffror === ''
          ? 'En inhyrd läggs in med sitt eget företags org.nr.'
          : 'Org.nr är tio siffror.';
    }

    if (Object.keys(brister).length > 0) {
      sattFaltfel(brister);
      return null;
    }
    sattFaltfel({});

    return {
      op_id: nyttOpId(),
      begaran:
        roll === 'guest'
          ? {
              email: adress,
              name: rensatNamn,
              role: roll,
              guest_org_number: tillOrgnummer(siffror, 'guest_org_number'),
            }
          : { email: adress, name: rensatNamn, role: roll },
    };
  };

  const skickaInbjudan = (): void => {
    const plan = inbjudan ?? byggInbjudan();
    if (plan === null) return;
    if (inbjudan === null) sattInbjudan(plan);

    sattSparar(true);
    sattFel(null);
    void (async () => {
      const svar = await bjudIn(firmaId, plan.op_id, plan.begaran);
      sattSparar(false);
      if (!svar.ok) {
        visaFel(svar.fel, () => sattInbjudan(null));
        return;
      }
      // ADD_MEMBER SKICKAR INGET MEJL. Beskedet nedan är enda stället personen
      // får veta att han själv måste hämta sin inloggningslänk.
      sattUpplagd({ namn: plan.begaran.name, epost: plan.begaran.email });
      sattInbjudan(null);
      sattEpost('');
      sattNamn('');
      sattOrgnr('');
      sattRoll('worker');
      laddaOm();
    })();
  };

  /* == Ändra en rad ====================================================== */

  const oppna = (m: MemberRow): void => {
    rensaBesked();
    sattUpplagd(null);
    sattVald(m.account_id);
    sattValdRoll(m.role);
    sattValdOrgnr(m.guest_org_number === null ? '' : visaOrgnummer(m.guest_org_number));
    sattRollplan(null);
    sattBortplan(null);
    sattBekraftaBort(false);
  };

  const stang = (): void => {
    rensaBesked();
    sattVald(null);
    sattRollplan(null);
    sattBortplan(null);
    sattBekraftaBort(false);
  };

  const byggRollplan = (kontoId: AccountId): Rollplan | null => {
    const siffror = rensaOrgnummer(valdOrgnr);
    if (valdRoll === 'guest' && !arOrgnummer(siffror)) {
      sattFaltfel({
        guest_org_number:
          siffror === ''
            ? 'En inhyrd läggs in med sitt eget företags org.nr.'
            : 'Org.nr är tio siffror.',
      });
      return null;
    }
    sattFaltfel({});
    return {
      kontoId,
      op_id: nyttOpId(),
      begaran:
        valdRoll === 'guest'
          ? { role: valdRoll, guest_org_number: tillOrgnummer(siffror, 'guest_org_number') }
          : { role: valdRoll },
    };
  };

  const sparaRollen = (kontoId: AccountId): void => {
    const plan = rollplan ?? byggRollplan(kontoId);
    if (plan === null) return;
    if (rollplan === null) sattRollplan(plan);

    sattSparar(true);
    sattFel(null);
    void (async () => {
      const svar = await bytRoll(firmaId, plan.kontoId, plan.op_id, plan.begaran);
      sattSparar(false);
      if (!svar.ok) {
        visaFel(svar.fel, () => sattRollplan(null));
        return;
      }
      stang();
      laddaOm();
    })();
  };

  const taBortRaden = (kontoId: AccountId): void => {
    const plan = bortplan ?? { kontoId, op_id: nyttOpId() };
    if (bortplan === null) sattBortplan(plan);

    sattSparar(true);
    sattFel(null);
    void (async () => {
      const svar = await taBort(firmaId, plan.kontoId, plan.op_id);
      sattSparar(false);
      if (!svar.ok) {
        visaFel(svar.fel, () => sattBortplan(null));
        return;
      }
      stang();
      laddaOm();
    })();
  };

  /* == Skärmen =========================================================== */

  if (lage.status === 'laddar') return <Laddar rader={5} />;
  if (lage.status === 'fel') {
    return (
      <Felruta
        fel={lage.fel}
        atgard={
          <Knapp variant="piller" onClick={laddaOm}>
            Försök igen
          </Knapp>
        }
      />
    );
  }

  const { medlemmar, betalda } = lage.lager;
  const valdMedlem = medlemmar.find((m) => m.account_id === vald) ?? null;
  const inbjudanLast = inbjudan !== null;
  const rollenLast = rollplan !== null;

  return (
    <>
      {!arChef && (
        <Yta rubrik="Du ser laget, du ändrar det inte">
          <p style={{ margin: 0 }}>
            Vem som är med i firman och vilken roll han har skrivs av chefen. Det
            är samma gräns som i attesten och på arbetsordrarna.
          </p>
        </Yta>
      )}

      <Yta rubrik="I firman" rakning={`${medlemmar.length} st`}>
        {medlemmar.map((m) => (
          <Personrad
            key={m.account_id}
            initialer={initialer(m.name)}
            namn={m.name}
            under={radtext(m)}
            // VARJE BELOPP BÄR SIN MOMSMÄRKNING DÄR DET STÅR. Personraden är
            // precis det chefen tittar på när han funderar på vad en montör till
            // kostar; att momsen står i en sammanfattande text längre ner
            // räcker inte. Perioden med, av samma skäl.
            varde={
              kostarPengar(m.role)
                ? `${visaOren(PRICE_PER_USER_ORE)}/mån ex moms`
                : '0 kr'
            }
            {...(arChef && !m.is_owner
              ? {
                  atgard: (
                    <Knapp
                      variant="piller-mini"
                      onClick={() => (m.account_id === vald ? stang() : oppna(m))}
                    >
                      {m.account_id === vald ? 'Stäng' : 'Ändra'}
                    </Knapp>
                  ),
                }
              : {})}
          />
        ))}
      </Yta>

      {/* PRISET, SOM EN MENING. Talet läses ur PRICE_PER_USER_ORE och skrivs
          aldrig för hand — beslut 18, och det är samma tal som fakturan. */}
      <Tomtext>
        {`${betalda} av ${medlemmar.length} kostar ${visaOren(PRICE_PER_USER_ORE)} per månad ex moms, `}
        bara genom att vara upplagda — också en månad då de inte rört appen.
        Inhyrda och bokförare kostar ingenting. Den som slutat tar du bort här,
        annars ligger han kvar på nästa dragning.
      </Tomtext>

      {fel !== null && <Felruta fel={fel} />}

      {/* == HAN MÅSTE HÄMTA LÄNKEN SJÄLV ===================================
          ADD_MEMBER skapar kontoraden och medlemskapet och INGENTING MER — det
          går inget mejl. Står inte det här kommer den han just lade upp aldrig
          in i appen, och chefen får aldrig veta varför. Rutan ligger direkt
          under listan och stannar tills han bockat av den. */}
      {upplagd !== null && (
        <Yta rubrik={`${upplagd.namn} är upplagd`}>
          <p style={{ margin: 0 }}>
            Klartid skickar inget mejl. Säg till honom att öppna appen och skriva{' '}
            <b>{upplagd.epost}</b> — då får han sin inloggningslänk dit. Gör han
            inte det kommer han aldrig in.
          </p>
          <div style={{ marginTop: '0.6rem' }}>
            <Knapp variant="piller-mini" onClick={() => sattUpplagd(null)}>
              Uppfattat
            </Knapp>
          </div>
        </Yta>
      )}

      {/* == ÄNDRA EN RAD =================================================== */}
      {arChef && valdMedlem !== null && (
        <>
          <Yta rubrik={`Ändra ${valdMedlem.name}`}>
            <Valfalt
              etikett="Roll"
              varde={valdRoll}
              onAndra={(nyRoll) => {
                sattValdRoll(nyRoll);
                // Ett halvtryckt "Ta bort" får inte ligga kvar och vänta medan
                // användaren håller på med något annat i samma ruta.
                sattBekraftaBort(false);
              }}
              val={ROLLVALEN}
              disabled={rollenLast || sparar}
              hjalp={prisbesked(valdRoll)}
            />
            {valdRoll === 'guest' && (
              <Falt
                etikett="Hans eget org.nr"
                varde={valdOrgnr}
                onAndra={sattValdOrgnr}
                disabled={rollenLast || sparar}
                inputMode="numeric"
                autoComplete="off"
                placeholder="5565551234"
                hjalp="Den inhyrde är anställd i ett annat företag. Numret får aldrig vara ditt eget."
                {...(faltfel['guest_org_number'] === undefined
                  ? {}
                  : { fel: faltfel['guest_org_number'] })}
              />
            )}
          </Yta>

          <Knapp
            disabled={arLast || sparar}
            onClick={() => sparaRollen(valdMedlem.account_id)}
            {...(arLast ? { title: LAST_TEXT } : {})}
          >
            {sparar ? 'Sparar ...' : rollenLast ? 'Försök igen' : 'Spara rollen'}
          </Knapp>

          {/* TVÅ OLIKA SAKER, OCH DE FÅR INTE SLÅS IHOP. Ligger en plan frusen
              är den andra knappen "Ändra uppgifterna": den SLÄPPER planen — och
              myntar därmed ett nytt op_id — och lämnar rutan öppen så att rollen
              går att rätta. Utan frusen plan är den bara "Avbryt". */}
          <div style={{ marginTop: '0.5rem' }}>
            <Knapp
              variant="stor-tyst"
              disabled={sparar}
              onClick={() => {
                if (!rollenLast) {
                  stang();
                  return;
                }
                sattRollplan(null);
                rensaBesked();
              }}
            >
              {rollenLast ? 'Ändra uppgifterna' : 'Avbryt'}
            </Knapp>
          </div>

          <Yta rubrik="Ta bort ur firman" tyst>
            <p style={{ margin: '0 0 0.5rem' }}>
              Hans timmar ligger kvar och står kvar med hans namn på gamla
              arbetsordrar. Det som händer är att han inte längre kommer in i
              appen — och att han slutar kosta från nästa dragning.
            </p>
            <Knapp
              variant="piller"
              ton={bekraftaBort ? 'varning' : 'normal'}
              disabled={arLast || sparar}
              onClick={() =>
                bekraftaBort ? taBortRaden(valdMedlem.account_id) : sattBekraftaBort(true)
              }
              {...(arLast ? { title: LAST_TEXT } : {})}
            >
              {bekraftaBort ? `Bekräfta: ta bort ${valdMedlem.name}` : 'Ta bort ur firman'}
            </Knapp>
          </Yta>
        </>
      )}

      {/* == BJUD IN ======================================================== */}
      {arChef && valdMedlem === null && (
        <>
          <Yta rubrik="Bjud in någon">
            <Falt
              etikett="Mejladress"
              varde={epost}
              onAndra={sattEpost}
              disabled={inbjudanLast || sparar}
              inputMode="email"
              autoComplete="off"
              placeholder="anton@ekror.se"
              hjalp="Det är den adress han loggar in med."
              {...(faltfel['email'] === undefined ? {} : { fel: faltfel['email'] })}
            />
            <Falt
              etikett="Namn"
              varde={namn}
              onAndra={sattNamn}
              disabled={inbjudanLast || sparar}
              autoComplete="off"
              placeholder="Anton Ek"
              hjalp="Namnet står bredvid varje rad han rapporterar."
              {...(faltfel['name'] === undefined ? {} : { fel: faltfel['name'] })}
            />
            <Valfalt
              etikett="Roll"
              varde={roll}
              onAndra={sattRoll}
              val={ROLLVALEN}
              disabled={inbjudanLast || sparar}
              hjalp={prisbesked(roll)}
              {...(faltfel['role'] === undefined ? {} : { fel: faltfel['role'] })}
            />
            {roll === 'guest' && (
              <Falt
                etikett="Hans eget org.nr"
                varde={orgnr}
                onAndra={sattOrgnr}
                disabled={inbjudanLast || sparar}
                inputMode="numeric"
                autoComplete="off"
                placeholder="5565551234"
                hjalp="Den inhyrde är anställd i ett annat företag. Numret får aldrig vara ditt eget."
                {...(faltfel['guest_org_number'] === undefined
                  ? {}
                  : { fel: faltfel['guest_org_number'] })}
              />
            )}
          </Yta>

          <Knapp
            disabled={arLast || sparar}
            onClick={skickaInbjudan}
            {...(arLast ? { title: LAST_TEXT } : {})}
          >
            {sparar ? 'Lägger upp ...' : inbjudanLast ? 'Försök igen' : 'Bjud in'}
          </Knapp>

          {inbjudanLast && !sparar && (
            <div style={{ marginTop: '0.5rem' }}>
              <Knapp
                variant="stor-tyst"
                onClick={() => {
                  sattInbjudan(null);
                  rensaBesked();
                }}
              >
                Ändra uppgifterna
              </Knapp>
            </div>
          )}

          {/* BARA VID NÄTFEL — det enda läget där vi inte vet om anropet gick
              fram, och alltså det enda där skillnaden mellan "Försök igen" och
              "Ändra uppgifterna" spelar roll. */}
          {inbjudanLast && !sparar && fel !== null && fel.slag === 'nat' && (
            <Tomtext>
              Kom inget svar? Tryck Försök igen — inbjudan bär samma id, så han
              kan inte bli upplagd två gånger. Ändrar du uppgifterna i stället
              får den ett nytt id.
            </Tomtext>
          )}
        </>
      )}

      {arChef && (
        <Tomtext>
          Den som äger firman kan varken få en annan roll eller tas bort — det är
          han som betalar, och firman måste alltid ha en ägare som är chef. Ska
          någon annan ta över görs det inte här än.
        </Tomtext>
      )}
    </>
  );
};
